import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Zap, Check, ShoppingCart, RefreshCw, BarChart3, ArrowLeftRight, Layers, ShieldCheck, ArrowRight } from "lucide-react";
import api from "@/lib/api";

const FEATURES = [
  { icon: Layers, title: "One unified inventory", body: "Every single, slab and sealed product in one place — synced across Shopify and TCGPlayer so you never double-sell." },
  { icon: RefreshCw, title: "Condition-aware repricing", body: "Auto-reprice NM off TCGPlayer market and LP–DMG off live listing data, on your own daily schedule." },
  { icon: ShoppingCart, title: "Buying, Selling & Trade POS", body: "Run card-show transactions from your phone with QR scanning, store-credit math and payment gates." },
  { icon: BarChart3, title: "Sales & trade analytics", body: "See revenue, units sold, trade value out and trade profit — with per-transaction detail." },
  { icon: ArrowLeftRight, title: "Boxed.gg buylist built in", body: "Instantly see which cards Boxed is buying and what they'll pay, right inside your inventory." },
  { icon: ShieldCheck, title: "Team roles & QR labels", body: "Invite staff with scoped roles and print QR label stickers for fast, accurate pulls." },
];

function PlanCard({ plan, featured, cycle }) {
  return (
    <div
      data-testid={`landing-plan-${plan.id}`}
      className={`relative rounded-2xl p-7 flex flex-col ${featured ? "ring-2 ring-amber-500" : "border"}`}
      style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }}
    >
      {featured && (
        <span className="absolute -top-3 left-7 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider bg-amber-500 text-[#1a1205]">
          Most popular
        </span>
      )}
      <div className="font-heading text-lg font-bold text-slate-100">{plan.name}</div>
      <div className="mt-3 flex items-end gap-1">
        <span className="font-heading text-4xl font-extrabold text-slate-50">${cycle === "yearly" && plan.price_yearly ? plan.price_yearly : plan.price}</span>
        <span className="text-slate-500 text-sm mb-1">{plan.price === 0 ? "/mo" : cycle === "yearly" && plan.price_yearly ? "/yr" : "/mo"}</span>
      </div>
      {cycle === "yearly" && plan.price_yearly && (
        <div className="text-xs text-emerald-400 mt-1">Save ${plan.price * 12 - plan.price_yearly}/yr vs monthly</div>
      )}
      <p className="text-sm text-slate-400 mt-2 min-h-[40px]">{plan.blurb}</p>
      <ul className="mt-5 space-y-2.5 flex-1">
        {plan.features.map((f, i) => (
          <li key={i} className="flex items-start gap-2.5 text-sm text-slate-300">
            <Check className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" /> {f}
          </li>
        ))}
      </ul>
      <Link
        to="/login"
        data-testid={`landing-plan-cta-${plan.id}`}
        className={`mt-6 w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-sm transition-colors ${
          featured ? "bg-amber-500 text-[#1a1205] hover:bg-amber-400" : "border text-slate-200 hover:bg-[var(--pk-hover)]"
        }`}
        style={featured ? {} : { borderColor: "var(--pk-border)" }}
      >
        {plan.price === 0 ? "Start free" : "Choose " + plan.name} <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  );
}

export default function Landing() {
  const [pricing, setPricing] = useState(null);
  const [cycle, setCycle] = useState("monthly");

  useEffect(() => {
    api.get("/pricing").then(({ data }) => setPricing(data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen" style={{ background: "var(--pk-bg)" }}>
      {/* Nav */}
      <header className="sticky top-0 z-30 border-b backdrop-blur-xl" style={{ borderColor: "var(--pk-border)", background: "rgba(9,11,16,0.75)" }}>
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-lg flex items-center justify-center holo-shine border" style={{ borderColor: "var(--pk-border)" }}>
              <Zap className="h-5 w-5 text-amber-400" />
            </div>
            <span className="font-heading font-extrabold text-lg text-slate-100">TCG Vendor Hub</span>
          </div>
          <div className="flex items-center gap-2">
            <a href="#pricing" className="hidden sm:inline text-sm text-slate-400 hover:text-slate-100 px-3 py-2">Pricing</a>
            <Link to="/admin" data-testid="landing-admin-link" className="hidden sm:inline text-sm text-slate-400 hover:text-slate-100 px-3 py-2">Admin</Link>
            <Link to="/login" data-testid="landing-signin" className="text-sm font-semibold px-4 py-2 rounded-xl bg-amber-500 text-[#1a1205] hover:bg-amber-400 transition-colors">
              Sign in
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1613771404784-3a5686aa2be3?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="" className="absolute inset-0 w-full h-full object-cover opacity-[0.12]"
        />
        <div className="absolute inset-0" style={{ background: "radial-gradient(1200px 500px at 70% -10%, rgba(245,158,11,0.10), transparent), linear-gradient(180deg, rgba(9,11,16,0.2), var(--pk-bg))" }} />
        <div className="relative max-w-6xl mx-auto px-5 pt-20 pb-16">
          <div className="max-w-2xl animate-fade-up">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border text-amber-300" style={{ borderColor: "var(--pk-border)", background: "var(--pk-card)" }}>
              <Zap className="h-3.5 w-3.5" /> The operating system for Pokémon TCG vendors
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-50 leading-[1.05] mt-6">
              Sell your cards everywhere,<br /><span className="text-amber-400">manage them once.</span>
            </h1>
            <p className="text-slate-400 mt-5 text-base leading-relaxed max-w-xl">
              TCG Vendor Hub unifies your singles, slabs and sealed across Shopify and TCGPlayer — with repricing,
              in-person POS, trade-ins, buylist intel and team tools built for card shops.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/login" data-testid="hero-start" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm bg-amber-500 text-[#1a1205] hover:bg-amber-400 transition-colors">
                Start free <ArrowRight className="h-4 w-4" />
              </Link>
              <a href="#pricing" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm border text-slate-200 hover:bg-[var(--pk-hover)] transition-colors" style={{ borderColor: "var(--pk-border)" }}>
                View pricing
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-5 py-16">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {FEATURES.map((f, i) => (
            <div key={i} className="rounded-2xl p-6 border" style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }}>
              <div className="h-10 w-10 rounded-lg flex items-center justify-center holo-shine border mb-4" style={{ borderColor: "var(--pk-border)" }}>
                <f.icon className="h-5 w-5 text-amber-400" />
              </div>
              <div className="font-heading font-bold text-slate-100">{f.title}</div>
              <p className="text-sm text-slate-400 mt-2 leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="max-w-6xl mx-auto px-5 py-16">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="font-heading text-3xl sm:text-4xl font-extrabold text-slate-50">Simple pricing that scales with your shop</h2>
          <p className="text-slate-400 mt-3 text-base">Start free. Upgrade when you outgrow it. Add only what you need.</p>
        </div>

        <div className="flex justify-center mt-8">
          <div className="inline-flex items-center gap-1 p-1 rounded-xl border" style={{ borderColor: "var(--pk-border)", background: "var(--pk-card)" }} data-testid="landing-cycle-toggle">
            {[["monthly", "Monthly"], ["yearly", "Yearly · 2 months free"]].map(([v, l]) => (
              <button key={v} onClick={() => setCycle(v)} data-testid={`landing-cycle-${v}`}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${cycle === v ? "bg-amber-500 text-[#1a1205]" : "text-slate-400 hover:text-slate-100"}`}>{l}</button>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mt-8">
          {pricing?.plans?.map((p) => <PlanCard key={p.id} plan={p} featured={p.id === "hobby"} cycle={cycle} />)}
        </div>

        {/* Add-ons */}
        <div className="mt-14">
          <h3 className="font-heading text-xl font-bold text-slate-100 text-center">Power-ups &amp; add-ons</h3>
          <p className="text-slate-500 text-sm text-center mt-1">Bolt these onto any plan.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mt-7">
            {pricing?.slab_tiers?.map((t) => (
              <div key={t.id} className="rounded-xl p-5 border" style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }}>
                <div className="font-heading font-bold text-slate-100 text-sm">{t.name}</div>
                <div className="font-heading text-2xl font-extrabold text-slate-50 mt-2">${t.price}<span className="text-slate-500 text-sm font-normal">/mo</span></div>
                <div className="text-xs text-slate-400 mt-1">{t.quota.toLocaleString()} slab lookups / mo</div>
              </div>
            ))}
            {pricing?.addons?.map((a) => (
              <div key={a.id} className="rounded-xl p-5 border" style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }}>
                <div className="font-heading font-bold text-slate-100 text-sm">{a.name}</div>
                <div className="font-heading text-2xl font-extrabold text-slate-50 mt-2">${a.price}<span className="text-slate-500 text-sm font-normal">/mo</span></div>
                <div className="text-xs text-slate-400 mt-1">Optional add-on</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="border-t mt-10" style={{ borderColor: "var(--pk-border)" }}>
        <div className="max-w-6xl mx-auto px-5 py-8 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-slate-400">
            <Zap className="h-4 w-4 text-amber-400" /> <span className="font-heading font-bold text-slate-200">TCG Vendor Hub</span>
          </div>
          <div className="flex items-center gap-5 text-sm text-slate-500">
            <a href="#pricing" className="hover:text-slate-200">Pricing</a>
            <Link to="/admin" className="hover:text-slate-200">Admin</Link>
            <Link to="/login" className="hover:text-slate-200">Sign in</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
